// Contrat d'API v1 (docs/Spec POC web.md, « Contrat d'API (v1) »).
// La simulation MSW et le futur serveur renvoient exactement ces formes :
// l'interface ne dépend que de ce fichier.

export type Species = 'cepe' | 'girolle';

/** Date civile au format AAAA-MM-JJ, heure de Paris. */
export type IsoDate = string;

export type ScoreLabel = 'peu probable' | 'possible' | 'favorable' | 'très favorable' | 'excellent';

export type CellStatus = 'public' | 'prive' | 'interdit';

/**
 * GET /api/v1/scores?species=cepe&date=AAAA-MM-JJ
 * Extension compatible (proposée) : &resolution=9|10&bbox=ouest,sud,est,nord pour les sous-mailles
 * affichées au zoom proche. Sans ces paramètres, la réponse reste celle du contrat v1 (résolution 8, toute la zone).
 */
export interface ScoresResponse {
  species: Species;
  /** Résolution H3 des mailles renvoyées : 8 par défaut. */
  resolution: number;
  /** Date demandée. */
  date: IsoDate;
  /** Date réellement servie : la veille si le calcul du matin n'est pas encore passé. */
  data_date: IsoDate;
  forecast: boolean;
  /** Horodatage ISO 8601 du calcul. */
  generated_at: string;
  /** Mailles boisées hors cœur de Parc ; les autres n'ont pas de score. */
  cells: ScoreCell[];
}

export interface ScoreCell {
  h3: string;
  score: number;
}

export interface Factor {
  text: string;
  effect: 'plus' | 'moins';
  /** Extension compatible (proposée) : d'où vient la donnée, par ex. « Météo-France AROME » ou « BD Forêt V2 ». */
  source?: string;
}

export interface CellConditions {
  rain_14d_mm: number;
  soil_temp_c: number;
  altitude_m: number;
  tree: string;
  forest_age: 'ancienne' | 'récente';
}

export interface TimelinePoint {
  date: IsoDate;
  score: number;
  rain_mm: number;
  forecast: boolean;
}

/** GET /api/v1/cells/{h3}?species=cepe&date=AAAA-MM-JJ — ne contient jamais de coordonnées de trouvailles. */
export interface CellDetail {
  h3: string;
  species: Species;
  date: IsoDate;
  score: number;
  label: ScoreLabel;
  trend_3d: number;
  place_name: string;
  status: CellStatus;
  factors: Factor[];
  conditions: CellConditions;
  timeline: TimelinePoint[];
}

/** GET /api/v1/timeline?species=cepe&bbox=ouest,sud,est,nord */
export interface TimelineResponse {
  species: Species;
  bbox: [number, number, number, number];
  today: IsoDate;
  days: TimelinePoint[];
}

/** GET /api/v1/zone */
export interface ZoneResponse {
  name: string;
  bbox: [number, number, number, number];
  /** Contour de la zone test. */
  outline: GeoJSON.Polygon;
  /** Cœur du Parc national des Pyrénées, découpé sur la zone : cueillette interdite. */
  park_core: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  h3_resolution: number;
  today: IsoDate;
  attribution: string;
}

export interface Find {
  id: string;
  species: Species;
  count: number;
  note?: string;
  /** Photo envoyée à part ; l'API ne renvoie qu'une référence. */
  photo_id?: string;
  found_at: string;
  /** Maille publique de la trouvaille. La position exacte reste côté serveur, jamais renvoyée. */
  h3: string;
}

/** POST /api/v1/outings — position exacte transmise une seule fois, stockée côté serveur. */
export interface OutingInput {
  id: string;
  started_at: string;
  ended_at: string;
  /** Trace GPS [longitude, latitude, horodatage ms]. */
  track: [number, number, number][];
  distance_m: number;
  finds: (Omit<Find, 'h3'> & { lon: number; lat: number })[];
}

/**
 * GET /api/v1/outings — mes sorties.
 * Extension proposée : DELETE /api/v1/outings/{id} (204), pour l'effacement demandé par la spec.
 */
export interface Outing {
  id: string;
  started_at: string;
  ended_at: string;
  duration_s: number;
  distance_m: number;
  /** Trace simplifiée, à l'échelle de la mini-carte. */
  track_preview: [number, number][];
  finds: Find[];
}

export interface OutingsResponse {
  outings: Outing[];
}

export interface ApiError {
  error: string;
  message: string;
}

export const scoreLabel = (score: number): ScoreLabel =>
  score >= 80
    ? 'excellent'
    : score >= 60
      ? 'très favorable'
      : score >= 40
        ? 'favorable'
        : score >= 20
          ? 'possible'
          : 'peu probable';
